import { useMemo, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Inbox, Search, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { FadeIn } from "@/components/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = "/api"; // same-origin: ingress routes /api to the backend

const STATUSES = [
  { value: "new", label: "New", cls: "bg-brand-light text-brand" },
  { value: "contacted", label: "Contacted", cls: "bg-amber-100 text-amber-800" },
  { value: "in_progress", label: "In Progress", cls: "bg-blue-100 text-blue-800" },
  { value: "closed", label: "Closed", cls: "bg-sage-light text-sage-dark" },
];

const statusMeta = (v) => STATUSES.find((s) => s.value === v) || STATUSES[0];

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("gws_admin_key") || "");
  const [inquiries, setInquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");

  const load = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/inquiries`, {
        headers: { "X-Admin-Key": key },
      });
      localStorage.setItem("gws_admin_key", key);
      setInquiries(res.data.inquiries);
    } catch (err) {
      setError(
        err.response?.status === 401
          ? "Incorrect access key. Please try again."
          : "Could not load inquiries. Please try again."
      );
      setInquiries(null);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/inquiries/${id}`,
        { status },
        { headers: { "X-Admin-Key": key } }
      );
      setInquiries((list) =>
        list.map((q) => (q.id === id ? { ...q, status } : q))
      );
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete the inquiry from ${q.full_name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/inquiries/${q.id}`, {
        headers: { "X-Admin-Key": key },
      });
      setInquiries((list) => list.filter((x) => x.id !== q.id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Could not delete inquiry");
    }
  };

  const countries = useMemo(() => {
    if (!inquiries) return [];
    return [...new Set(inquiries.map((q) => q.country).filter(Boolean))].sort();
  }, [inquiries]);

  const filtered = useMemo(() => {
    if (!inquiries) return [];
    const term = search.trim().toLowerCase();
    return inquiries.filter((q) => {
      if (countryFilter !== "all" && q.country !== countryFilter) return false;
      if (!term) return true;
      return [q.full_name, q.email, q.phone, q.treatment, q.message, q.country]
        .filter(Boolean)
        .some((f) => f.toLowerCase().includes(term));
    });
  }, [inquiries, search, countryFilter]);

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Country", "Treatment", "Status", "Message", "Submitted (UTC)"],
      ...filtered.map((q) => [
        q.full_name,
        q.email,
        q.phone,
        q.country,
        q.treatment,
        statusMeta(q.status).label,
        q.message,
        q.created_at || "",
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `getwellsoon-inquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <>
      <SEO
        title="Inquiries | GetWellSoon"
        description="Internal inquiry dashboard for GetWellSoon coordinators."
      />
      <section className="bg-pearl py-20 md:py-28" data-testid="admin-page">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <FadeIn>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              Internal
            </p>
            <h1 className="mt-4 font-serif text-4xl tracking-tight text-ink">
              Patient inquiries
            </h1>
          </FadeIn>

          {inquiries === null ? (
            <FadeIn delay={0.1}>
              <form
                onSubmit={load}
                className="mt-10 max-w-md rounded-3xl border border-border bg-white p-8"
                data-testid="admin-login-form"
              >
                <Label htmlFor="admin-key">Access key</Label>
                <div className="mt-2 flex gap-3">
                  <Input
                    id="admin-key"
                    type="password"
                    data-testid="admin-key-input"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    placeholder="Enter access key"
                    className="h-12 bg-gray-50"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="admin-login-button"
                    className="h-12 rounded-full bg-brand px-6 font-semibold text-white hover:bg-brand-dark"
                  >
                    <Lock className="mr-2 h-4 w-4" />
                    {loading ? "..." : "View"}
                  </Button>
                </div>
                {error && (
                  <p className="mt-4 text-sm font-semibold text-red-600" data-testid="admin-error">
                    {error}
                  </p>
                )}
              </form>
            </FadeIn>
          ) : (
            <FadeIn>
              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm font-semibold text-ink" data-testid="admin-count">
                  <Inbox className="mr-2 inline h-4 w-4 text-brand" />
                  {filtered.length} of {inquiries.length}{" "}
                  {inquiries.length === 1 ? "inquiry" : "inquiries"}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={exportCsv}
                    data-testid="admin-export-csv-button"
                    className="rounded-full border-ink/15 px-5 font-semibold hover:border-brand hover:text-brand"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => load()}
                    disabled={loading}
                    data-testid="admin-refresh-button"
                    className="rounded-full border-ink/15 px-5 font-semibold hover:border-brand hover:text-brand"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh
                  </Button>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search name, email, phone, treatment…"
                    data-testid="admin-search-input"
                    className="h-12 rounded-full border-border bg-white pl-11"
                  />
                </div>
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger
                    data-testid="admin-country-filter"
                    className="h-12 w-full rounded-full border-border bg-white sm:w-56"
                  >
                    <SelectValue placeholder="All countries" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">All countries</SelectItem>
                    {countries.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-6 space-y-4" data-testid="admin-inquiry-list">
                {filtered.map((q, i) => (
                  <div
                    key={q.id || i}
                    className="rounded-3xl border border-border bg-white p-6"
                    data-testid={`admin-inquiry-${i}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="font-serif text-xl tracking-tight text-ink">
                        {q.full_name}
                        <span className="ml-3 rounded-full bg-gray-100 px-3 py-1 align-middle font-sans text-xs font-bold text-ink/70">
                          {q.treatment}
                        </span>
                      </p>
                      <div className="flex items-center gap-2">
                        <Select
                          value={q.status || "new"}
                          onValueChange={(v) => updateStatus(q.id, v)}
                        >
                          <SelectTrigger
                            data-testid={`admin-status-${i}`}
                            className={`h-9 w-36 rounded-full border-0 text-xs font-bold ${statusMeta(q.status).cls}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {STATUSES.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          onClick={() => remove(q)}
                          data-testid={`admin-delete-${i}`}
                          aria-label={`Delete inquiry from ${q.full_name}`}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
                      <span>{q.email}</span>
                      <span>{q.phone}</span>
                      <span>{q.country}</span>
                      <span>
                        {q.created_at
                          ? new Date(q.created_at).toLocaleString("en-US", {
                              dateStyle: "medium",
                              timeStyle: "short",
                            })
                          : ""}
                      </span>
                    </div>
                    <p className="mt-4 border-t border-border pt-4 text-sm leading-relaxed text-ink/80">
                      {q.message}
                    </p>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <p
                    className="rounded-3xl border border-border bg-white p-10 text-center text-sm text-muted-foreground"
                    data-testid="admin-empty"
                  >
                    {inquiries.length === 0
                      ? "No inquiries yet."
                      : "No inquiries match your search."}
                  </p>
                )}
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </>
  );
}
